import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, LabRequest } from '../../types';
import { FlaskConical, CheckCircle, Printer, XCircle, CalendarClock, History } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isAMHTest, hcgStructure, isHCGTest, isHCGAbnormal } from '../../store/labStructures';
export const LaboratoryDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    logAudit
  } = useData();
  const [selectedRequest, setSelectedRequest] = useState<{
    enc: Encounter;
    lab: LabRequest;
  } | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  const [labComments, setLabComments] = useState(''); // Added state for lab comments
  // Modals state
  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    item: {
      enc: Encounter;
      lab: LabRequest;
    } | null;
    reason: string;
  }>({
    isOpen: false,
    item: null,
    reason: ''
  });
  const [rescheduleModal, setRescheduleModal] = useState<{
    isOpen: boolean;
    item: {
      enc: Encounter;
      lab: LabRequest;
    } | null;
    newDate: string;
    reason: string;
  }>({
    isOpen: false,
    item: null,
    newDate: '',
    reason: ''
  });
  const [showHistory, setShowHistory] = useState(false);
  // Find encounters with pending lab requests
  const pendingRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Pending').map(lab => ({
    enc,
    lab
  })));
  const rescheduledRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Rescheduled').map(lab => ({
    enc,
    lab
  })));
  const historyRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Cancelled' || lab.status === 'Rescheduled').map(lab => ({
    enc,
    lab
  }))).sort((a, b) => {
    const timeA = new Date(a.lab.cancelledAt || a.lab.rescheduledAt || 0).getTime();
    const timeB = new Date(b.lab.cancelledAt || b.lab.rescheduledAt || 0).getTime();
    return timeB - timeA;
  });
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectRequest = (item: {
    enc: Encounter;
    lab: LabRequest;
  }) => {
    setSelectedRequest(item);
    setLabComments(item.lab.labComments || ''); // Initialize labComments from existing labComments
    const initialResults: Record<string, string> = {
      ...(item.lab.results || {})
    };
    const hasFBC = item.lab.tests.includes('Full Blood Count') || item.lab.tests.includes('FBC');
    const hasUrinalysis = item.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = item.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    const hasHormonalProfile = item.lab.tests.some(t => t.toLowerCase().includes('hormonal profile'));
    const hasAMH = item.lab.tests.some(t => isAMHTest(t));
    // Only initialize empty strings for keys that don't already have a saved value
    const ensureKey = (k: string) => {
      if (initialResults[k] === undefined) initialResults[k] = '';
    };
    item.lab.tests.forEach(test => {
      if ((test === 'Full Blood Count' || test === 'FBC') && hasFBC) {
        fbcStructure.forEach(cat => {
          cat.tests.forEach(t => ensureKey(t.name));
        });
      } else if (test.toLowerCase().includes('urinalysis') && hasUrinalysis) {
        urinalysisTests.forEach(t => ensureKey(`UA_${t.name}`));
      } else if (test.toLowerCase().includes('testosterone') && hasTestosterone) {
        testosteroneTests.forEach(t => ensureKey(`TESTO_${t.name}`));
      } else if (test.toLowerCase().includes('hormonal profile') && hasHormonalProfile) {
        hormonalProfileTests.forEach(h => ensureKey(`HP_${h.key}`));
      } else if (isAMHTest(test) && hasAMH) {
        ensureKey(amhStructure.storageKey);
      } else if (isHCGTest(test)) {
        ensureKey(hcgStructure.storageKey);
      } else {
        ensureKey(test);
      }
    });
    if (item.lab.otherTests) ensureKey('Others');
    setResults(initialResults);
  };
  const handleSaveResults = () => {
    if (!selectedRequest || !currentUser) return;
    const {
      enc,
      lab
    } = selectedRequest;
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Completed',
      results,
      labComments,
      performedBy: currentUser.fullName,
      completedAt: new Date().toISOString(),
      completedBy: currentUser.id
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    // Check if all labs are completed
    const allLabsCompleted = updatedLabRequests.every(l => l.status === 'Completed');
    const updateData: Partial<Encounter> = {
      labRequests: updatedLabRequests,
      pendingLab: !allLabsCompleted
    };
    // If all labs done and no pending radiology, move back to clinician
    if (allLabsCompleted && !enc.pendingRadiology) {
      updateData.stage = 'CLINICIAN';
    }
    updateEncounter(enc.id, updateData);
    alert('Results saved and sent to Clinician.');
    setSelectedRequest(null);
  };
  const handleCancelConfirm = () => {
    if (!cancelModal.item || !currentUser || !cancelModal.reason.trim()) return;
    const {
      enc,
      lab
    } = cancelModal.item;
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Cancelled',
      cancelledAt: new Date().toISOString(),
      cancelledBy: currentUser.id,
      cancellationReason: cancelModal.reason
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    const allLabsCompletedOrCancelled = updatedLabRequests.every(l => l.status === 'Completed' || l.status === 'Cancelled');
    updateEncounter(enc.id, {
      labRequests: updatedLabRequests,
      pendingLab: !allLabsCompletedOrCancelled
    });
    const patient = getPatient(enc.patientId);
    logAudit({
      type: 'lab.cancel',
      encounterId: enc.id,
      patientId: enc.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: cancelModal.reason,
      labRequestId: lab.id
    });
    setCancelModal({
      isOpen: false,
      item: null,
      reason: ''
    });
  };
  const handleRescheduleConfirm = () => {
    if (!rescheduleModal.item || !currentUser || !rescheduleModal.reason.trim() || !rescheduleModal.newDate) return;
    const {
      enc,
      lab
    } = rescheduleModal.item;
    const now = new Date().toISOString();
    const newHistoryEntry = {
      from: lab.rescheduledFor || lab.requestedAt,
      to: rescheduleModal.newDate,
      at: now,
      by: currentUser.id,
      reason: rescheduleModal.reason
    };
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Rescheduled',
      rescheduledFor: rescheduleModal.newDate,
      rescheduledAt: now,
      rescheduledBy: currentUser.id,
      rescheduleReason: rescheduleModal.reason,
      rescheduleHistory: [...(lab.rescheduleHistory || []), newHistoryEntry]
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    updateEncounter(enc.id, {
      labRequests: updatedLabRequests
    });
    const patient = getPatient(enc.patientId);
    logAudit({
      type: 'lab.reschedule',
      encounterId: enc.id,
      patientId: enc.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: rescheduleModal.reason,
      labRequestId: lab.id,
      previousDate: lab.rescheduledFor || lab.requestedAt,
      newDate: rescheduleModal.newDate
    });
    setRescheduleModal({
      isOpen: false,
      item: null,
      newDate: '',
      reason: ''
    });
  };
  const handlePrint = () => {
    window.print();
  };
  if (selectedRequest) {
    const patient = getPatient(selectedRequest.enc.patientId);
    if (!patient) return <div data-id="element-6733" data-mp="2j7iyquwqe2dj">Patient not found</div>;
    const hasFBC = selectedRequest.lab.tests.includes('Full Blood Count') || selectedRequest.lab.tests.includes('FBC');
    const hasUrinalysis = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    const hasHormonalProfile = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('hormonal profile'));
    const hasAMH = selectedRequest.lab.tests.some(t => isAMHTest(t));
    const hasHCG = selectedRequest.lab.tests.some(t => isHCGTest(t));
    const otherTests = selectedRequest.lab.tests.filter(t => t !== 'Full Blood Count' && t !== 'FBC' && !t.toLowerCase().includes('urinalysis') && !t.toLowerCase().includes('testosterone') && !t.toLowerCase().includes('hormonal profile') && !isAMHTest(t) && !isHCGTest(t));
    return <div className="space-y-6 pb-20" data-id="element-6734" data-mp="lfhs8nzplt6z">
        <div className="flex justify-between items-center print:hidden sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-6735" data-mp="21qlrw3z8ii2m">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-6736" data-mp="1ws2561cth297">
            Enter Laboratory Results
          </h1>
          <div className="flex space-x-2" data-id="element-6737" data-mp="3v0ruiaf81ql8">
            <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-6738" data-mp="3pz2lf0bk7mzs">
              Back to Queue
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-6739" data-mp="3l7jceuzh1ar6">
              <Printer className="w-4 h-4 mr-2" data-id="element-6740" data-mp="3h7shqg6tqahd" /> Print Results
            </Button>
            <Button onClick={handleSaveResults} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-6741" data-mp="3c864w7uwqw93">
              <CheckCircle className="w-5 h-5 mr-2" data-id="element-6742" data-mp="378bee5f1o8ok" /> Save & Send to Clinician
            </Button>
          </div>
        </div>

        {/* Printable Area */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 print:border-none print:shadow-none print:p-0" data-id="element-6743" data-mp="2y8obff6o2ute">
          {/* Print Header */}
          <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-6" data-id="element-6744" data-mp="10htzwom8q11n">
            <div className="flex items-center" data-id="element-6745" data-mp="whtak8xls5ft">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-24 w-24 object-contain mr-4" data-id="element-6746" data-mp="2ef6ip4hxvcfu" />
              <div data-id="element-6747" data-mp="2o4zsbzyithhg">
                <h1 className="text-3xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-6748" data-mp="2o6kmhogxayes">
                  BMED MEDICAL CENTRE
                </h1>
                <p className="text-sm italic text-gray-600" data-id="element-6749" data-mp="1vgkfnk2v3b9a">
                  Caring Healing Leading
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-6750" data-mp="20gqata8cbfw4">
              <p className="font-bold text-xl uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-6751" data-mp="24g7ga3nrod1a">
                LABORATORY REPORT
              </p>
              <p className="text-sm" data-id="element-6752" data-mp="29d7ejsedkbhu">
                <span className="font-semibold" data-id="element-6753" data-mp="3fwfbfk0vcm2x">Date:</span>{' '}
                {new Date().toLocaleDateString()}
              </p>
              <p className="text-sm" data-id="element-6754" data-mp="3kt5tue05btri">
                <span className="font-semibold" data-id="element-6755" data-mp="1kshe0evm585w">File No:</span>{' '}
                {patient.bmcFileNumber}
              </p>
            </div>
          </div>

          {/* Patient Details */}
          <div className="border-2 border-black p-4 mb-8 text-sm space-y-3" data-id="element-6756" data-mp="1uqnb8j6h5q4f">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-6757" data-mp="1uokny6wpib3e">
              <div className="col-span-8" data-id="element-6758" data-mp="1ps3o4fg5a8ah">
                <span className="font-semibold" data-id="element-6759" data-mp="121q5ffjg9lsk">Patient Name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-2" data-id="element-6760" data-mp="x4hm3bmkfbpq">
                <span className="font-semibold" data-id="element-6761" data-mp="1brsxndiqyvpu">Age:</span> {patient.age}
              </div>
              <div className="col-span-2" data-id="element-6762" data-mp="1btm1ym5w13uo">
                <span className="font-semibold" data-id="element-6763" data-mp="1701fnrdnpybb">Sex:</span> {patient.sex}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-6764" data-mp="ja73zd52qfsn">
              <div className="col-span-6" data-id="element-6765" data-mp="ecf4ny23nref">
                <span className="font-semibold" data-id="element-6766" data-mp="ebm4jfxnc059">Requested By:</span>{' '}
                {selectedRequest.enc.lastAttendingClinician}
              </div>
              <div className="col-span-6" data-id="element-6767" data-mp="2uqh56dv77sbl">
                <span className="font-semibold" data-id="element-6768" data-mp="2ur8zzmgz1m0t">Requested On:</span>{' '}
                {new Date(selectedRequest.lab.requestedAt).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-8" data-id="element-6769" data-mp="2kybbmzpat2ua">
            {hasFBC && <div data-id="element-6770" data-mp="38rjpom6jouke">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-6771" data-mp="38r1odb50zq5m">
                  Full Blood Count + Differential (FBC + DC)
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-6772" data-mp="2zosf0olhu2tt">
                  <thead data-id="element-6773" data-mp="34o57bov4w23s">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-6774" data-mp="34nby8h8pd0ih">
                      <th className="p-2 text-left font-semibold" data-id="element-6775" data-mp="1oa5cutf254nx">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-6776" data-mp="1tb2sozclhmm9">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-6777" data-mp="28kzek8jraadf">
                        Reference range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-6778" data-mp="1ysk0vnfhs7yc">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-6779" data-mp="1utb6cgcmo61q">
                    {fbcStructure.map(category => <Fragment key={category.category} data-id="element-6780" data-mp="2rgy026ibhabh">
                        <tr data-id="element-6781" data-mp="2miwfti95n4yf">
                          <td colSpan={4} className="p-2 font-bold bg-gray-50 uppercase pt-4" data-id="element-6782" data-mp="2mio43torq5ot">
                            {category.category}
                          </td>
                        </tr>
                        {category.tests.map(test => <tr key={test.name} className="border-b border-gray-100" data-id="element-6783" data-mp="173grjt8r07eu">
                            <td className="p-2 pl-4" data-id="element-6784" data-mp="3drh6bnwyv209">{test.name}</td>
                            <td className="p-2" data-id="element-6785" data-mp="3ip97z9ney2hn">
                              <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test.name] || ''} onChange={e => setResults({
                        ...results,
                        [test.name]: e.target.value
                      })} placeholder="Enter value" data-id="element-6786" data-mp="3iq0xc2qf33zh" />
                            </td>
                            <td className="p-2 text-gray-600" data-id="element-6787" data-mp="2g2e7p0rxr54q">{test.range}</td>
                            <td className="p-2 text-gray-600" data-id="element-6788" data-mp="2g4h1lvw7lieq">{test.unit}</td>
                          </tr>)}
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasUrinalysis && <div data-id="element-6789" data-mp="3oco1x9li1txs">
                <h3 className="font-bold text-lg mb-3 uppercase text-center border-b-2 border-black pb-1" data-id="element-6790" data-mp="3oc4uimth6ny0">
                  Urinalysis Report
                </h3>
                <div className="max-w-md mx-auto space-y-2 py-4" data-id="element-6791" data-mp="64ypnqck14fo">
                  {urinalysisTests.map(test => <div key={test.name} className="flex items-center" data-id="element-6792" data-mp="fwl6q3bkpxqc">
                      <span className="w-16 font-bold text-sm" data-id="element-6793" data-mp="fyxke4aatlxl">
                        {test.name}
                      </span>
                      <span className="mx-3 text-gray-400" data-id="element-6794" data-mp="ox9jyvt57u26">-</span>
                      <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium text-sm" value={results[`UA_${test.name}`] || ''} onChange={e => setResults({
                  ...results,
                  [`UA_${test.name}`]: e.target.value
                })} placeholder="Enter result" data-id="element-6795" data-mp="ovgdphmfzbup" />
                    </div>)}
                </div>
                <div className="max-w-md mx-auto mt-8 grid grid-cols-2 gap-4 text-sm" data-id="element-6796" data-mp="pm9zrt52d653">
                  <div data-id="element-6797" data-mp="athlb51fw1ek">
                    <label className="block font-semibold mb-1" data-id="element-6798" data-mp="fudyyablf1x6">
                      Dr.'s Name:
                    </label>
                    <input type="text" className="w-full p-1 border-b border-black focus:outline-none print:border-black" value={results['UA_DR_NAME'] || ''} onChange={e => setResults({
                  ...results,
                  ['UA_DR_NAME']: e.target.value
                })} data-id="element-6799" data-mp="1umgnxe1mzdd" />
                  </div>
                  <div data-id="element-6800" data-mp="8cz8gxrii1fw">
                    <label className="block font-semibold mb-1" data-id="element-6801" data-mp="8bfs775uvqe4">
                      Signature:
                    </label>
                    <input type="text" className="w-full p-1 border-b border-black focus:outline-none print:border-black" value={results['UA_DR_SIG'] || ''} onChange={e => setResults({
                  ...results,
                  ['UA_DR_SIG']: e.target.value
                })} placeholder="Initials / signature" data-id="element-6802" data-mp="3f7jocm5yvfx" />
                  </div>
                </div>
              </div>}

            {hasTestosterone && <div data-id="element-6803" data-mp="1w9gurfjc10dj">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-6804" data-mp="1w8xe025f4i13">
                  Testosterone
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-6805" data-mp="209r0qs74jqse">
                  <thead data-id="element-6806" data-mp="25a4ynhf0hkcp">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-6807" data-mp="258anuvxgf8mc">
                      <th className="p-2 text-left font-semibold" data-id="element-6808" data-mp="18lz11egibs7x">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-6809" data-mp="1hl2jcg7uo3ld">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-6810" data-mp="1mc2djdpl47k6">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-6811" data-mp="ovvivbou53xh">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-6812" data-mp="tt592tk1tjmd">
                    {testosteroneTests.map(test => <Fragment key={test.name} data-id="element-6813" data-mp="1c4mgvghtwi4">
                        <tr className="border-b border-gray-100" data-id="element-6814" data-mp="5avn3ck63w9z">
                          <td className="p-2 font-medium" data-id="element-6815" data-mp="5dhqr13xtrmx">{test.name}</td>
                          <td className="p-2" data-id="element-6816" data-mp="abja7n59zsyx">
                            <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[`TESTO_${test.name}`] || ''} onChange={e => setResults({
                        ...results,
                        [`TESTO_${test.name}`]: e.target.value
                      })} placeholder="Enter value" data-id="element-6817" data-mp="facr7pjru23u" />
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-6818" data-mp="2sg10oxeglpgo">
                            <div data-id="element-6819" data-mp="2nh6gps4r9uuz">Males: {test.rangeMale}</div>
                            <div data-id="element-6820" data-mp="2nflnq6y3ajz8">Females: {test.rangeFemale}</div>
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-6821" data-mp="2ik6a9g3zuq3c">{test.unit}</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50" data-id="element-6822" data-mp="2dlmkt5q6yw68">
                          <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-6823" data-mp="29izrt1hsb57g">
                            Method: {test.method}
                          </td>
                        </tr>
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasHormonalProfile && <div data-id="element-6824" data-mp="1lrubkl7xmawm">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-6825" data-mp="1ltdteg98ku0i">
                  Hormonal Profile
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-6826" data-mp="1g627nja55rkn">
                  <thead data-id="element-6827" data-mp="1g7dc7lbev3ct">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-6828" data-mp="126ammyl7jax5">
                      <th className="p-2 text-left font-semibold" data-id="element-6829" data-mp="1285b2mvotl5z">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-6830" data-mp="1uv85b2tbz9qf">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-6831" data-mp="1yvi8y8tnho24">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold w-20" data-id="element-6832" data-mp="1l32e3vawkwca">
                        Units
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-6833" data-mp="2ds9b2pupldvf">
                    {hormonalProfileTests.map(hormone => <Fragment key={hormone.key} data-id="element-6834" data-mp="2ilc7r6us93fu">
                        <tr className="bg-gray-50" data-id="element-6835" data-mp="2rzcjxnglily0">
                          <td colSpan={4} className="p-2 font-bold uppercase" data-id="element-6836" data-mp="2ry1frmj8nc6d">
                            {hormone.name}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100 align-top" data-id="element-6837" data-mp="31o48dlmg6o46">
                          <td className="p-2 font-medium" data-id="element-6838" data-mp="2wqceim28srxc">{hormone.name}</td>
                          <td className="p-2" data-id="element-6839" data-mp="2wsf7neyoz16a">
                            <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[`HP_${hormone.key}`] || ''} onChange={e => setResults({
                        ...results,
                        [`HP_${hormone.key}`]: e.target.value
                      })} placeholder="Enter value" data-id="element-6840" data-mp="291jhem9vwe15" />
                          </td>
                          <td className="p-2 text-gray-600 text-xs" data-id="element-6841" data-mp="23ykcvq98bhyi">
                            {hormone.ranges.map(r => <div key={r.label} data-id="element-6842" data-mp="2cxxbtozq8o4z">
                                <span className="font-medium" data-id="element-6843" data-mp="2cyqloytzftrc">{r.label}:</span>{' '}
                                {r.value}
                              </div>)}
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-6844" data-mp="2mt8tog6fmbgy">{hormone.unit}</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50" data-id="element-6845" data-mp="2wleu8ear217r">
                          <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-6846" data-mp="2wnhiasscdk9u">
                            Method: {hormone.method}
                          </td>
                        </tr>
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasAMH && <div data-id="element-6847" data-mp="kxjfzroox8po">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-6848" data-mp="kz2zdr5n7zvo">
                  Anti Mullerian Hormone (AMH)
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-6849" data-mp="1cozae7whnzqg">
                  <thead data-id="element-6850" data-mp="1crbq0eerxy0c">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-6851" data-mp="18nvw3pu3abrc">
                      <th className="p-2 text-left font-semibold" data-id="element-6852" data-mp="18pqgn31g81uw">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-6853" data-mp="yycc2gun4buw">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-6854" data-mp="1wdzyjtvjk5gr">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold w-20" data-id="element-6855" data-mp="1rkn9xjqv3yo0">
                        Units
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-6856" data-mp="2fh1ri2vq49dy">
                    <tr className="bg-gray-50" data-id="element-6857" data-mp="21qeuqg9iwtki">
                      <td colSpan={4} className="p-2 font-bold uppercase" data-id="element-6858" data-mp="21pn8vx5qaa7f">
                        Anti Mullerian Hormone (AMH)
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 align-top" data-id="element-6859" data-mp="1rrzdd50asyo7">
                      <td className="p-2 font-medium" data-id="element-6860" data-mp="1wrc0lqv307rb">{amhStructure.name}</td>
                      <td className="p-2" data-id="element-6861" data-mp="1wp90uf44n09e">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[amhStructure.storageKey] || ''} onChange={e => setResults({
                      ...results,
                      [amhStructure.storageKey]: e.target.value
                    })} placeholder="Enter value" data-id="element-6862" data-mp="1iscb6gzqvou3" />
                      </td>
                      <td className="p-2 text-gray-600 text-xs" data-id="element-6863" data-mp="1aiofiivj1fjp">
                        <div className="font-medium mb-1" data-id="element-6864" data-mp="1ah3jeotkdojq">By age (ng/mL):</div>
                        {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-6865" data-mp="1k7og26pogj00">
                            <span className="font-medium" data-id="element-6866" data-mp="1kb2jlia2uy91">{a.ageBand}:</span>{' '}
                            {a.range}
                          </div>)}
                      </td>
                      <td className="p-2 text-gray-600" data-id="element-6867" data-mp="1t7lc4teeck53">{amhStructure.unit}</td>
                    </tr>
                    <tr className="border-b border-gray-200 bg-gray-50" data-id="element-6868" data-mp="3wazdsn34xat">
                      <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-6869" data-mp="3v2luz99bt5ur">
                        Method: {amhStructure.method}
                      </td>
                    </tr>
                    <tr data-id="element-6870" data-mp="3ix57cmrhf9ki">
                      <td colSpan={4} className="p-2 text-xs text-gray-600 bg-amber-50 border-t border-amber-200" data-id="element-6871" data-mp="3ixyhlxpngh7e">
                        <p className="font-bold uppercase mb-1" data-id="element-6872" data-mp="2r7k8sfwhtv2l">
                          Interpretation
                        </p>
                        <ul className="list-disc pl-5 space-y-0.5" data-id="element-6873" data-mp="2v9x2g5xyu2j0">
                          {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-6874" data-mp="356a69wn85wrh" data-mpi={i}>{line}</li>)}
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>}

            {hasHCG && <div data-id="element-6875" data-mp="khm57vux9bk7">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-6876" data-mp="24xk4h5r3mh67">
                  Beta HCG
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-6877" data-mp="1zz8vsycjicpk">
                  <thead data-id="element-6878" data-mp="1v78ryutcmznp">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-6879" data-mp="1v6fs8dst5af2">
                      <th className="p-2 text-left font-semibold" data-id="element-6880" data-mp="1q9pcxsotuiup">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-6881" data-mp="1m8nbd57ftupk">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold w-72" data-id="element-6882" data-mp="1c9696xre9yyc">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold w-20" data-id="element-6883" data-mp="17b4ok8eblrkr">
                        Units
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-6884" data-mp="31nku16zal1ok">
                    <tr className="bg-gray-50" data-id="element-6885" data-mp="31pe1upln5ry5">
                      <td colSpan={4} className="p-2 font-bold uppercase" data-id="element-6886" data-mp="3teiajs7bkbsd">
                        Beta HCG
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 align-top" data-id="element-6887" data-mp="3oevvrebuenhg">
                      <td className="p-2 font-medium" data-id="element-6888" data-mp="6z6zsigxauuk">{hcgStructure.name}</td>
                      <td className="p-2" data-id="element-6889" data-mp="20461u6e8b1w">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[hcgStructure.storageKey] || ''} onChange={e => setResults({
                      ...results,
                      [hcgStructure.storageKey]: e.target.value
                    })} placeholder="Enter value" data-id="element-6890" data-mp="20cijkylteck" />
                      </td>
                      <td className="p-2 text-gray-600 text-xs" data-id="element-6891" data-mp="126fxz636k9vk">
                        {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-6892" data-mp="1750zr9y2z1m4">
                            <span className="font-medium" data-id="element-6893" data-mp="t84c1hd9e09g">{r.label}:</span>{' '}
                            {r.range}
                          </div>)}
                      </td>
                      <td className="p-2 text-gray-600" data-id="element-6894" data-mp="1ky8rr2q2pz4c">{hcgStructure.unit}</td>
                    </tr>
                    <tr className="border-b border-gray-200 bg-gray-50" data-id="element-6895" data-mp="1pwk0fa4mu3kz">
                      <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-6896" data-mp="1c6gigvcscw1c">
                        Method: {hcgStructure.method}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>}

            {(otherTests.length > 0 || selectedRequest.lab.otherTests) && <div data-id="element-6897" data-mp="2ikd2lue0fb1j">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-6898" data-mp="2iis3rsknx5xv">
                  Other Investigations
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-6899" data-mp="29j54bcbhua7g">
                  <thead data-id="element-6900" data-mp="29lhta9fo5ku0">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-6901" data-mp="24kkdg3i4t2vo">
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-6902" data-mp="24merqyywdwqs">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-6903" data-mp="1uu8y7kszz79h">
                        Result
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-6904" data-mp="1lu2o88wicpf2">
                    {otherTests.map(test => <tr key={test} className="border-b border-gray-100" data-id="element-6905" data-mp="1lrq0ff42jku3">
                        <td className="p-2 font-medium" data-id="element-6906" data-mp="1gu802d44adxp">{test}</td>
                        <td className="p-2" data-id="element-6907" data-mp="1bvd6cg4m34yt">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test] || ''} onChange={e => setResults({
                      ...results,
                      [test]: e.target.value
                    })} placeholder="Enter result..." data-id="element-6908" data-mp="1bxpwvhofqnnh" />
                        </td>
                      </tr>)}
                    {selectedRequest.lab.otherTests && <tr className="border-b border-gray-100" data-id="element-6909" data-mp="xs7g831e4mfl">
                        <td className="p-2 font-medium" data-id="element-6910" data-mp="9nxd64aic7e">
                          Others: {selectedRequest.lab.otherTests}
                        </td>
                        <td className="p-2" data-id="element-6911" data-mp="26yzagl36pmcj">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results['Others'] || ''} onChange={e => setResults({
                      ...results,
                      ['Others']: e.target.value
                    })} placeholder="Enter result..." data-id="element-6912" data-mp="2700xlnxh5zam" />
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* Comments / Observations / Interpretation */}
          <div className="mt-8" data-id="element-6913" data-mp="10bkiho5wkdkv">
            <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-6914" data-mp="10cnci4yiuw50">
              Comments / Observations / Interpretation
            </h3>
            <textarea className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0" rows={4} placeholder="Enter any additional observations, interpretations, or comments..." value={labComments} onChange={e => setLabComments(e.target.value)} // Handle input for labComments
          data-id="element-6915" data-mp="3gvxs4k1ejs2m" />
          </div>

          {/* Footer Signatures */}
          <div className="mt-16 pt-8 border-t border-gray-300" data-id="element-6916" data-mp="2jaieu8rnxbaq">
            <div className="flex justify-between items-end" data-id="element-6917" data-mp="2o7s51qmvlqzm">
              <div className="w-64" data-id="element-6918" data-mp="2oaedrtli6cjf">
                <p className="text-sm font-semibold mb-8" data-id="element-6919" data-mp="hhc94l1rjdel">
                  Laboratory Technologist:
                </p>
                <div className="border-b border-black" data-id="element-6920" data-mp="mfng8o0ob9t4"></div>
                <p className="text-sm mt-2 font-medium" data-id="element-6921" data-mp="kzi1hoghja0u">
                  {currentUser?.fullName}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-6922" data-mp="g0f5sumeag5j">
                  BMED Laboratory Dept
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6 print:hidden" data-id="element-6923" data-mp="711tzvastfhn">
      <div className="flex justify-between items-center" data-id="element-6924" data-mp="72cxrv48un8r">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-6925" data-mp="13oy36f3wp8iu">
          Laboratory Dashboard
        </h1>
        <div className="flex space-x-2" data-id="element-6926" data-mp="yshd3fd5ck34">
          <Badge variant="warning" className="text-sm px-3 py-1" data-id="element-6927" data-mp="ytiu00gug0sr">
            {pendingRequests.length} Pending
          </Badge>
          {rescheduledRequests.length > 0 && <Badge variant="info" className="text-sm px-3 py-1 bg-orange-100 text-orange-800" data-id="element-6928" data-mp="pymaof3et7r6">
              {rescheduledRequests.length} Rescheduled
            </Badge>}
        </div>
      </div>

      <div className="space-y-6" data-id="element-6929" data-mp="30dbplwbo73mx">
        <Card title="Pending Requests" noPadding data-id="element-6930" data-mp="30ed307g5s27t">
          <ul className="divide-y divide-gray-200" data-id="element-6931" data-mp="35blmcbd1mcan">
            {pendingRequests.length > 0 ? pendingRequests.map(item => {
            const patient = getPatient(item.enc.patientId);
            if (!patient) return null;
            return <li key={item.lab.id} className="p-4 hover:bg-gray-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4" data-id="element-6932" data-mp="3ea72jzv1lf89">
                    <div className="flex items-center" data-id="element-6933" data-mp="3o8ehye9onp85">
                      <div className="bg-yellow-100 p-2 rounded-full mr-4" data-id="element-6934" data-mp="3t2r26qizh4ky">
                        <FlaskConical className="h-6 w-6 text-yellow-600" data-id="element-6935" data-mp="xwxipplnndl" />
                      </div>
                      <div data-id="element-6936" data-mp="5xjbj1d957ng">
                        <p className="text-sm font-medium text-blue-600" data-id="element-6937" data-mp="5yb92hqetfgd">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-6938" data-mp="3hxtl5xubvy3i">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-6939" data-mp="3d131l0jbekdc">
                          Tests: {item.lab.tests.join(', ')}{' '}
                          {item.lab.otherTests ? `+ ${item.lab.otherTests}` : ''}
                        </p>
                        <p className="text-xs text-gray-400 mt-1" data-id="element-6940" data-mp="2z7u0ro9fn76f">
                          Requested:{' '}
                          {new Date(item.lab.requestedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end" data-id="element-6941" data-mp="3384ddjrqcvu9">
                      <Button size="sm" variant="outline" onClick={() => setRescheduleModal({
                  isOpen: true,
                  item,
                  newDate: '',
                  reason: ''
                })} data-id="element-6942" data-mp="2fjcph4l6azna">
                        <CalendarClock className="w-4 h-4 mr-1" data-id="element-6943" data-mp="1rgy47vbcowg4" /> Reschedule
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelModal({
                  isOpen: true,
                  item,
                  reason: ''
                })} data-id="element-6944" data-mp="1wfrgxlitcjf8">
                        <XCircle className="w-4 h-4 mr-1" data-id="element-6945" data-mp="yqpsal7er89z" /> Cancel
                      </Button>
                      <Button onClick={() => handleSelectRequest(item)} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-6946" data-mp="13q2autr8xt6r">
                        Enter Results
                      </Button>
                    </div>
                  </li>;
          }) : <li className="p-12 text-center text-gray-500" data-id="element-6947" data-mp="2yrl46g1dur2q">
                <FlaskConical className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-6948" data-mp="32w2ku6cg4o9a" />
                <p className="text-lg font-medium text-gray-900" data-id="element-6949" data-mp="3qklscjno8x0b">
                  No pending requests
                </p>
              </li>}
          </ul>
        </Card>

        {rescheduledRequests.length > 0 && <Card title="Rescheduled Requests" noPadding data-id="element-6950" data-mp="3hjnl7shxs9di">
            <ul className="divide-y divide-gray-200" data-id="element-6951" data-mp="3miqsljle2ron">
              {rescheduledRequests.map(item => {
            const patient = getPatient(item.enc.patientId);
            if (!patient) return null;
            return <li key={item.lab.id} className="p-4 bg-orange-50/50 hover:bg-orange-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 border-l-4 border-orange-400" data-id="element-6952" data-mp="2b5od6rv10862">
                    <div className="flex items-center" data-id="element-6953" data-mp="2cl0igiovf4ci">
                      <div className="bg-orange-100 p-2 rounded-full mr-4" data-id="element-6954" data-mp="2ckhb1vwujycq">
                        <CalendarClock className="h-6 w-6 text-orange-600" data-id="element-6955" data-mp="27n79xj03hy92" />
                      </div>
                      <div data-id="element-6956" data-mp="1jxd0zciwctu3">
                        <p className="text-sm font-medium text-blue-600" data-id="element-6957" data-mp="1juihbn7pjd1q">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-6958" data-mp="1tmo50kqxf8b4">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-6959" data-mp="1163rv4d1g731">
                          Tests: {item.lab.tests.join(', ')}{' '}
                          {item.lab.otherTests ? `+ ${item.lab.otherTests}` : ''}
                        </p>
                        <div className="mt-1 flex items-center gap-2" data-id="element-6960" data-mp="2w7fiic3899iw">
                          <Badge variant="warning" className="bg-orange-100 text-orange-800" data-id="element-6961" data-mp="316ifd99209fs">
                            Rescheduled to:{' '}
                            {new Date(item.lab.rescheduledFor!).toLocaleString()}
                          </Badge>
                          <span className="text-xs text-gray-500 italic" data-id="element-6962" data-mp="2i9qyf6gzi6ew">
                            Reason: {item.lab.rescheduleReason}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end" data-id="element-6963" data-mp="1ld44kkasfnj">
                      <Button size="sm" variant="outline" onClick={() => setRescheduleModal({
                  isOpen: true,
                  item,
                  newDate: '',
                  reason: ''
                })} data-id="element-6964" data-mp="6jy4cjzjuzbz">
                        <CalendarClock className="w-4 h-4 mr-1" data-id="element-6965" data-mp="2x9r6u462mef" /> Reschedule
                        Again
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelModal({
                  isOpen: true,
                  item,
                  reason: ''
                })} data-id="element-6966" data-mp="3u4cf9copkckf">
                        <XCircle className="w-4 h-4 mr-1" data-id="element-6967" data-mp="1ebdifsjddwfs" /> Cancel
                      </Button>
                      <Button onClick={() => handleSelectRequest(item)} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-6968" data-mp="1lek20km9mipq">
                        Enter Results
                      </Button>
                    </div>
                  </li>;
          })}
            </ul>
          </Card>}

        <Card data-id="element-6969" data-mp="12ntzwy4iqzxx">
          <div className="flex justify-between items-center cursor-pointer" onClick={() => setShowHistory(!showHistory)} data-id="element-6970" data-mp="12nas4a8l1rxm">
            <div className="flex items-center gap-2" data-id="element-6971" data-mp="17gvef50tcxgz">
              <History className="w-5 h-5 text-gray-500" data-id="element-6972" data-mp="srh4nvtbse12" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-6973" data-mp="xozh3w6jxbdt">
                Cancelled & Rescheduled History
              </h3>
            </div>
            <Button variant="outline" size="sm" data-id="element-6974" data-mp="jpzsgoj1gwhn">
              {showHistory ? 'Hide' : 'Show'}
            </Button>
          </div>

          {showHistory && <div className="mt-4 border-t border-gray-200 pt-4" data-id="element-6975" data-mp="529avy9cswk1">
              {historyRequests.length > 0 ? <ul className="space-y-3" data-id="element-6976" data-mp="4pvuw4u0fr1">
                  {historyRequests.map((item, idx) => {
              const patient = getPatient(item.enc.patientId);
              const isCancelled = item.lab.status === 'Cancelled';
              return <li key={`${item.lab.id}-${idx}`} className="text-sm bg-gray-50 p-3 rounded border border-gray-200" data-id="element-6977" data-mp="o1dfmutv445w" data-mpi={idx}>
                        <div className="flex justify-between mb-1" data-id="element-6978" data-mp="xr6qtuq3eh9m">
                          <span className="font-medium" data-id="element-6979" data-mp="xtj4hvoti5gv">
                            {patient?.firstName} {patient?.lastName} (
                            {patient?.bmcFileNumber})
                          </span>
                          <span className="text-gray-500 text-xs" data-id="element-6980" data-mp="16ps7tlfx1o8a">
                            {new Date(isCancelled ? item.lab.cancelledAt! : item.lab.rescheduledAt!).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-1" data-id="element-6981" data-mp="8f0nt1cgn6u3">
                          Tests:{' '}
                          <span className={isCancelled ? 'line-through text-red-700 opacity-70' : ''} data-id="element-6982" data-mp="d8bmgf74chax">
                            {item.lab.tests.join(', ')} {item.lab.otherTests}
                          </span>
                        </p>
                        <div className="flex items-center gap-2 mt-2" data-id="element-6983" data-mp="w3jxnlb5rroa">
                          <Badge variant={isCancelled ? 'danger' : 'warning'} className={!isCancelled ? 'bg-orange-100 text-orange-800' : ''} data-id="element-6984" data-mp="2tx8rnxv1fw6t">
                            {isCancelled ? 'Cancelled' : 'Rescheduled'}
                          </Badge>
                          <span className="text-gray-600 italic text-xs" data-id="element-6985" data-mp="2cmvd1wmnrr04">
                            Reason:{' '}
                            {isCancelled ? item.lab.cancellationReason : item.lab.rescheduleReason}
                          </span>
                        </div>
                      </li>;
            })}
                </ul> : <p className="text-gray-500 text-sm text-center py-4" data-id="element-6986" data-mp="2vcbd6iuflhg0">
                  No history available.
                </p>}
            </div>}
        </Card>
      </div>

      {/* Modals */}
      {cancelModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-6987" data-mp="3jze8ja41upmf">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-6988" data-mp="3o20vawlv5kcr">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-red-600" data-id="element-6989" data-mp="3a1q39q8thzq0">
              <XCircle className="w-5 h-5 mr-2" data-id="element-6990" data-mp="3f0t8pbspm7yi" /> Cancel Laboratory Request
            </h3>
            <p className="text-sm text-gray-600 mb-4" data-id="element-6991" data-mp="6l13qo7njq9u">
              Are you sure you want to cancel this request? This action will be
              logged in the audit trail.
            </p>
            <div className="mb-4" data-id="element-6992" data-mp="blx7n1s07cf9">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6993" data-mp="3t1dqc4mw1xqf">
                Reason for cancellation (required)
              </label>
              <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" rows={3} value={cancelModal.reason} onChange={e => setCancelModal({
            ...cancelModal,
            reason: e.target.value
          })} placeholder="e.g., Clinician requested by mistake, patient refused..." data-id="element-6994" data-mp="1s7ze1g3ijya" />
            </div>
            <div className="flex justify-end space-x-3" data-id="element-6995" data-mp="2jrze0t0kuzx0">
              <Button variant="outline" onClick={() => setCancelModal({
            isOpen: false,
            item: null,
            reason: ''
          })} data-id="element-6996" data-mp="2jsiue5akxg8x">
                Go Back
              </Button>
              <Button variant="danger" onClick={handleCancelConfirm} disabled={!cancelModal.reason.trim()} data-id="element-6997" data-mp="250grecd7q74o">
                Confirm Cancellation
              </Button>
            </div>
          </div>
        </div>}

      {rescheduleModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-6998" data-mp="3q5zo0akupss3">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-6999" data-mp="3m3d3kuqifa4x">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-orange-600" data-id="element-7000" data-mp="3h4s4iymzuwi2">
              <CalendarClock className="w-5 h-5 mr-2" data-id="element-7001" data-mp="3c5ozhe70kqa3" /> Reschedule Laboratory
              Request
            </h3>
            <div className="space-y-4" data-id="element-7002" data-mp="doky4kd72l5l">
              <div data-id="element-7003" data-mp="dnjjy70vtijn">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7004" data-mp="dpw2alajxux8">
                  New scheduled date & time
                </label>
                <input type="datetime-local" className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none" value={rescheduleModal.newDate} onChange={e => setRescheduleModal({
              ...rescheduleModal,
              newDate: e.target.value
            })} data-id="element-7005" data-mp="3x86ntvb5x03" />
              </div>
              <div data-id="element-7006" data-mp="13xacpe8bz0he">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7007" data-mp="13yv8f76dspgu">
                  Reason for rescheduling (required)
                </label>
                <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none" rows={3} value={rescheduleModal.reason} onChange={e => setRescheduleModal({
              ...rescheduleModal,
              reason: e.target.value
            })} placeholder="e.g., Reagents out of stock, patient unavailable..." data-id="element-7008" data-mp="1dvzwxdnp8x0t" />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-7009" data-mp="25nfeuueb9vkp">
              <Button variant="outline" onClick={() => setRescheduleModal({
            isOpen: false,
            item: null,
            newDate: '',
            reason: ''
          })} data-id="element-7010" data-mp="25nyuu5kei9w3">
                Go Back
              </Button>
              <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={handleRescheduleConfirm} disabled={!rescheduleModal.reason.trim() || !rescheduleModal.newDate} data-id="element-7011" data-mp="ajr4b3yt08p5">
                Confirm Reschedule
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};